import React, { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Circle, Truck, MapPin, Clock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtUsd(cents) {
  if (cents === null || cents === undefined) return "—";
  return `$${(cents / 100).toFixed(2)}`;
}

const TIMELINE_STEPS = [
  { key: "created", label: "Order placed" },
  { key: "assigned", label: "Driver assigned" },
  { key: "picked_up", label: "Picked up" },
  { key: "in_transit", label: "On the way" },
  { key: "delivered", label: "Delivered" },
];

export default function Track() {
  const { orderId: paramId } = useParams();
  const [search] = useSearchParams();
  const [lookupId, setLookupId] = useState(paramId || "");
  const [token, setToken] = useState(search.get("t") || "");
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);

  const fetchState = useCallback(async (id, t) => {
    if (!id) return;
    setError(null);
    try {
      const r = await fetch(
        `${API}/api/public/tracking/${id}${t ? `?t=${encodeURIComponent(t)}` : ""}`
      );
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        setError(body.detail || "Could not load tracking.");
        setState(null);
        return;
      }
      setState(await r.json());
    } catch {
      setError("Network error.");
    }
  }, []);

  useEffect(() => {
    if (paramId) fetchState(paramId, search.get("t") || "");
  }, [paramId, search, fetchState]);

  // Poll every 12 seconds while the order is non-terminal
  useEffect(() => {
    if (!paramId || !state) return;
    if (["delivered", "failed", "cancelled"].includes(state.status)) return;
    const t = setInterval(() => fetchState(paramId, search.get("t") || ""), 12000);
    return () => clearInterval(t);
  }, [paramId, state, fetchState, search]);

  if (!paramId) {
    return (
      <div className="container mx-auto px-4 py-12 max-w-md" data-testid="track-lookup-page">
        <div className="glass-panel p-6">
          <h1 className="font-anton uppercase text-3xl text-white mb-2">Track your order</h1>
          <p className="text-lifted-dim text-sm mb-5">
            Paste the tracking link from your confirmation email, or enter your order ID.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!lookupId) return;
              window.location.href = `/track/${lookupId}${token ? `?t=${token}` : ""}`;
            }}
            className="space-y-3"
          >
            <input
              type="text"
              placeholder="Order ID"
              value={lookupId}
              onChange={(e) => setLookupId(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/15 text-white"
              data-testid="track-orderid-input"
            />
            <input
              type="text"
              placeholder="Tracking token (from URL)"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/15 text-white"
              data-testid="track-token-input"
            />
            <button className="btn-lifted w-full" data-testid="track-lookup-btn">
              Look up order
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-12 max-w-md" data-testid="track-error">
        <div className="glass-panel p-6">
          <h1 className="font-anton uppercase text-2xl text-white mb-2">Can't load this order</h1>
          <p className="text-lifted-dim text-sm">{error}</p>
          <Link to="/track" className="text-lifted-green underline text-sm mt-4 inline-block">
            ← Try another link
          </Link>
        </div>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="container mx-auto px-4 py-12 max-w-md">
        <p className="text-lifted-dim">Loading tracking…</p>
      </div>
    );
  }

  const currentIdx = TIMELINE_STEPS.findIndex((s) => s.key === state.status);

  return (
    <div className="container mx-auto px-4 py-8 lg:py-12 max-w-2xl" data-testid="track-page">
      <div className="glass-panel p-6 lg:p-8 mb-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-lifted-green mb-2">
          <Truck size={14} /> Lifted Supplies
        </div>
        <h1 className="font-anton uppercase text-3xl text-white">
          {state.status === "delivered"
            ? "Delivered."
            : state.status === "cancelled"
            ? "Cancelled."
            : "On its way."}
        </h1>
        {state.driver_name && state.status !== "delivered" && (
          <p className="text-lifted-dim text-sm mt-2">
            Driver: <span className="text-white">{state.driver_name}</span>
          </p>
        )}
        {state.eta_min_minutes && state.status !== "delivered" && (
          <p className="text-lifted-dim text-sm flex items-center gap-1 mt-1">
            <Clock size={14} /> ETA {state.eta_min_minutes}–{state.eta_max_minutes} min
          </p>
        )}
        {state.dropoff_address && (
          <p className="text-lifted-dim text-sm flex items-center gap-1 mt-1">
            <MapPin size={14} /> {state.dropoff_address}
          </p>
        )}
      </div>

      {/* Timeline */}
      <div className="glass-panel p-6 mb-6" data-testid="track-timeline">
        <ol className="relative border-l-2 border-white/10 pl-6 space-y-5">
          {TIMELINE_STEPS.map((s, idx) => {
            const reached = idx <= currentIdx;
            const isCurrent = idx === currentIdx;
            return (
              <li key={s.key} className="relative">
                <span
                  className={`absolute -left-[34px] mt-0.5 w-6 h-6 rounded-full flex items-center justify-center border-2 ${
                    reached
                      ? "bg-lifted-green/20 border-lifted-green text-lifted-green"
                      : "bg-black border-white/20 text-white/40"
                  }`}
                >
                  {reached ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                </span>
                <p
                  className={`font-bebas tracking-wider ${
                    reached ? "text-white" : "text-white/40"
                  } ${isCurrent ? "text-lifted-green" : ""}`}
                >
                  {s.label}
                </p>
              </li>
            );
          })}
        </ol>
      </div>

      {/* Order summary */}
      <div className="glass-panel p-6" data-testid="track-summary">
        <h3 className="font-anton uppercase text-xl text-white mb-3">Order summary</h3>
        <ul className="divide-y divide-white/10 mb-4">
          {(state.items || []).map((it, i) => (
            <li key={i} className="py-2 flex justify-between text-sm">
              <span className="text-white">
                {it.title || it.product_id} × {it.qty}
              </span>
              <span className="text-lifted-dim">
                {fmtUsd((it.unit_price_cents || 0) * (it.qty || 1))}
              </span>
            </li>
          ))}
        </ul>
        <div className="text-sm text-lifted-dim space-y-1">
          <div className="flex justify-between">
            <span>Subtotal</span>
            <span>{fmtUsd(state.subtotal_cents)}</span>
          </div>
          <div className="flex justify-between">
            <span>Delivery fee</span>
            <span>{fmtUsd(state.delivery_fee_cents)}</span>
          </div>
          <div className="flex justify-between">
            <span>Service fee</span>
            <span>{fmtUsd(state.service_fee_cents)}</span>
          </div>
          <div className="flex justify-between text-white font-bebas tracking-wider pt-2 border-t border-white/10 mt-2">
            <span>Total</span>
            <span>{fmtUsd(state.total_cents)}</span>
          </div>
        </div>
        <p className="text-xs text-lifted-dim mt-5">
          Updated {state.updated_at}
        </p>
      </div>
    </div>
  );
}
